import { classLangTypeSchema } from '@server/presentation'
import type { Locale } from '@shared/config/i18n'
import { getCourseDisplayTitle, getLocalizedBookTitle } from '@shared/utils'
import { z } from 'zod'

export const featuresSchema = z.enum(['REPLAY', 'REPORT', 'RESERVE'])
export const LESSON_CLASS_STATUS = {
  RESERVED: '예약',
  COMPLETED: '완료',
  CANCELED: '취소',
  ABSENT: '불참',
} as const
export const lessonClassStatusValues = [
  LESSON_CLASS_STATUS.RESERVED,
  LESSON_CLASS_STATUS.COMPLETED,
  LESSON_CLASS_STATUS.CANCELED,
  LESSON_CLASS_STATUS.ABSENT,
] as const
export const lessonClassStatusSchema = z.enum(lessonClassStatusValues)

const bookTitleSchema = z
  .union([z.string(), z.record(z.unknown())])
  .nullable()
  .optional()

export const rawLessonEntitySchema = z.object({
  class_id: z.string().nullable(),
  prestudy_class_id: z.string().nullable().optional(),
  student_user_id: z.string().nullable(),
  teacher_id: z.string().nullable(),
  teacher_name: z.string().nullable(),
  teacher_picture10: z.string().nullable(),
  teacher_user_id: z.string().nullable(),
  class_status: lessonClassStatusSchema.nullable(),
  class_date: z.string().nullable(),
  class_day_name: z.string().nullable(),
  class_start_time: z.string().nullable(),
  class_end_time: z.string().nullable(),
  class_start_datetime: z.string().nullable(),
  class_end_datetime: z.string().nullable(),
  unix_class_start_datetime: z.string().nullable(),
  unix_class_end_datetime: z.string().nullable(),
  lang_type: classLangTypeSchema,
  lang_type_name: z.string(),
  book_name: z.string().nullable().optional(),
  book_title: bookTitleSchema,
  trial_yn: z.string().nullable(),
  unlimit_yn: z.string().nullable(),
  class_type: z.enum(['PODO']),
  class_course_grade: z.string().nullable(),
  class_level: z.string(),
  class_level_name: z.string().nullable().optional(),
  class_week: z.string(),
  audio_file_id: z.string().nullable(),
  video_file_id: z.string().nullable(),
  class_course_id: z.string(),
  class_count: z.string().nullable(),
  comp_class_count: z.string().nullable(),
  ticket_expire_date: z.string().nullable(),
  pre_study_time: z.string().nullable(),
  class_state: z.string().nullable(),
  time_gap: z.string().nullable(),
  class_online_id: z.string().nullable(),
  service_type: z.string().nullable(),
  zoom_id: z.string().nullable(),
  try_cnt: z.string().nullable(),
  class_ticket_id: z.string().nullable(),
  invoice_status: z.string().nullable(),
  features: featuresSchema.array().optional().default([]),
})

export const lessonEntitySchema = z.object({
  classId: z.number().nullable(),
  prestudyClassId: z.number().nullable(),
  studentUserId: z.number().nullable(),
  teacherId: z.number().nullable(),
  teacherName: z.string().nullable(),
  teacherPicture10: z.string().nullable(),
  teacherUserId: z.number().nullable(),
  classStatus: lessonClassStatusSchema.nullable(),
  classDate: z.string().nullable(),
  classDayName: z.string().nullable(),
  classStartTime: z.string().nullable(),
  classEndTime: z.string().nullable(),
  classStartDatetime: z.string().nullable(),
  classEndDatetime: z.string().nullable(),
  unixClassStartDatetime: z.number().nullable(),
  unixClassEndDatetime: z.number().nullable(),
  langType: classLangTypeSchema,
  langTypeName: z.string(),
  bookName: z.string(),
  bookTitle: z.string().nullable(),
  trialYn: z.string().nullable(),
  unlimitYn: z.string().nullable(),
  classType: z.enum(['PODO']),
  classCourseGrade: z.string().nullable(),
  classLevel: z.number(),
  classLevelName: z.string(),
  classWeek: z.number(),
  audioFileId: z.string().nullable(),
  videoFileId: z.string().nullable(),
  classCourseId: z.number(),
  classCount: z.number(),
  compClassCount: z.number(),
  ticketExpireDate: z.string().nullable(),
  preStudyTime: z.number().nullable(),
  classState: z.string().nullable(),
  timeGap: z.string().nullable(),
  classOnlineId: z.string().nullable(),
  serviceType: z.string().nullable(),
  zoomId: z.string().nullable(),
  tryCnt: z.string().nullable(),
  classTicketId: z.string().nullable(),
  invoiceStatus: z.string().nullable(),
  features: featuresSchema.array().optional().default([]),
})

export const lessonEntityAdapter = (rawLessonEntity: RawLessonEntity, locale: Locale = 'ko'): LessonEntity => {
  const rawBookName = rawLessonEntity.book_name ?? ''
  const bookTitle = getLocalizedBookTitle(rawLessonEntity.book_title, rawBookName, locale)
  const displayBookName = bookTitle ?? rawBookName
  const classLevelName = getCourseDisplayTitle({
    classLevelName: rawLessonEntity.class_level_name,
    bookTitle: rawLessonEntity.book_title,
    bookName: rawBookName,
    locale,
    fallback: rawLessonEntity.class_level ? `Level ${rawLessonEntity.class_level}` : null,
  })

  return {
    classId: rawLessonEntity.class_id ? parseInt(rawLessonEntity.class_id, 10) : null,
    prestudyClassId: rawLessonEntity.prestudy_class_id ? parseInt(rawLessonEntity.prestudy_class_id, 10) : null,
    studentUserId: rawLessonEntity.student_user_id ? parseInt(rawLessonEntity.student_user_id, 10) : null,
    teacherId: rawLessonEntity.teacher_id ? parseInt(rawLessonEntity.teacher_id, 10) : null,
    teacherName: rawLessonEntity.teacher_name,
    teacherPicture10: rawLessonEntity.teacher_picture10,
    teacherUserId: rawLessonEntity.teacher_user_id ? parseInt(rawLessonEntity.teacher_user_id, 10) : null,
    classStatus: rawLessonEntity.class_status,
    classDate: rawLessonEntity.class_date,
    classDayName: rawLessonEntity.class_day_name,
    classStartTime: rawLessonEntity.class_start_time,
    classEndTime: rawLessonEntity.class_end_time,
    classStartDatetime: rawLessonEntity.class_start_datetime,
    classEndDatetime: rawLessonEntity.class_end_datetime,
    unixClassStartDatetime: rawLessonEntity.unix_class_start_datetime
      ? parseInt(rawLessonEntity.unix_class_start_datetime, 10)
      : null,
    unixClassEndDatetime: rawLessonEntity.unix_class_end_datetime
      ? parseInt(rawLessonEntity.unix_class_end_datetime, 10)
      : null,
    langType: rawLessonEntity.lang_type,
    langTypeName: rawLessonEntity.lang_type_name,
    bookName: displayBookName,
    bookTitle,
    trialYn: rawLessonEntity.trial_yn,
    unlimitYn: rawLessonEntity.unlimit_yn,
    classType: rawLessonEntity.class_type,
    classCourseGrade: rawLessonEntity.class_course_grade,
    classLevel: Number(rawLessonEntity.class_level),
    classLevelName,
    classWeek: parseInt(rawLessonEntity.class_week, 10),
    audioFileId: rawLessonEntity.audio_file_id,
    videoFileId: rawLessonEntity.video_file_id,
    classCourseId: parseInt(rawLessonEntity.class_course_id, 10),
    classCount: rawLessonEntity.class_count ? parseInt(rawLessonEntity.class_count, 10) : 0,
    compClassCount: rawLessonEntity.comp_class_count ? parseInt(rawLessonEntity.comp_class_count, 10) : 0,
    ticketExpireDate: rawLessonEntity.ticket_expire_date,
    preStudyTime: rawLessonEntity.pre_study_time ? parseInt(rawLessonEntity.pre_study_time, 10) : null,
    classState: rawLessonEntity.class_state,
    timeGap: rawLessonEntity.time_gap,
    classOnlineId: rawLessonEntity.class_online_id,
    serviceType: rawLessonEntity.service_type,
    zoomId: rawLessonEntity.zoom_id,
    tryCnt: rawLessonEntity.try_cnt,
    classTicketId: rawLessonEntity.class_ticket_id,
    invoiceStatus: rawLessonEntity.invoice_status,
    features: rawLessonEntity.features,
  } satisfies LessonEntity
}

export type RawLessonEntity = z.infer<typeof rawLessonEntitySchema>
export type LessonEntity = z.infer<typeof lessonEntitySchema>
export type LessonClassStatus = z.infer<typeof lessonClassStatusSchema>
